import { FormEvent, useEffect } from 'react';
import { Controller, FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import Card from '@/components/Card';
import { FormField } from '@/components/Form/FormField';
import { RichTextEditor } from '@/components/Custom/RichTextEditor';
import { Button } from '@/components/Button';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { getTranslatedItem } from '@/utils/language.utils';
import { setBranchRequiredFields } from '@/utils/required-fields/branch.utils';
import { useNavigate } from 'react-router-dom';

export const BranchWhiteLabelManagement = <TSchema extends FieldValues>({
  t,
  data,
  control,
  setValue,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const navigate = useNavigate();
  const translatedBranch = getTranslatedItem(data?.translatedBranches);

  useEffect(() => {
    if (!data?.company?.group?.isWhiteLabel) {
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setBranchRequiredFields({ setValue, data });
      setValue(
        'partFolderNotFound' as Path<TSchema>,
        (translatedBranch?.partFolderNotFound || data?.partFolderNotFound) ?? ''
      );
      setValue(
        'partContactUs' as Path<TSchema>,
        (translatedBranch?.partContactUs || data?.partContactUs) ?? ''
      );
      setValue(
        'proContactUs' as Path<TSchema>,
        (translatedBranch?.proContactUs || data?.proContactUs) ?? ''
      );
    }
  }, [setValue, data, translatedBranch]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card
        cardClassName="mb-5"
        title={
          <TitleSection
            text={t('common:infoLinkedForPart')}
            defaultIconClassName="ki-notepad-bookmark text-[25px]"
          />
        }
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:referenceNumberNotFound')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="partFolderNotFound"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
        <FormField
          containerClassName="mb-5"
          text={t('common:contactUs')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="partContactUs"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
      <Card
        title={t('common:infoLinkedForPro')}
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
      >
        <FormField
          containerClassName="mb-5"
          text={t('common:contactUs')}
          textWrapperClassName="mt-3"
        >
          <Controller
            name="proContactUs"
            control={control}
            render={({ field }) => (
              <RichTextEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </FormField>
      </Card>
    </form>
  );
};
